import {
  HymnFormatInput,
  buildCcliTxt,
  buildEasyWorshipTxt,
  buildOpenLyricsXml,
  copyToClipboard,
  sanitizeFilename,
  triggerFileDownload,
} from "@/utils";
import { Check, Copy, Download } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

interface WorshipExportersProps {
  hymn: HymnFormatInput;
  className?: string;
  size?: "sm" | "md";
}

export const WorshipExporters: React.FC<WorshipExportersProps> = ({
  hymn,
  className = "",
  size = "md",
}) => {
  const [copiedType, setCopiedType] = useState<string | null>(null);

  const numStr =
    hymn.number != null ? String(hymn.number).padStart(3, "0") : "";
  const baseTitle = numStr
    ? `${numStr} - ${hymn.title || "Hymn"}`
    : hymn.title || "Hymn";
  const filenameBase = sanitizeFilename(baseTitle);

  const handleCopy = async (
    type: "ew" | "ol" | "ccli",
    textGetter: () => string,
    label: string,
  ) => {
    const text = textGetter();
    const success = await copyToClipboard(text);
    if (success) {
      setCopiedType(type);
      toast.success(`${label} copié dans le presse-papier !`);
      setTimeout(() => setCopiedType(null), 2500);
    } else {
      toast.error(`Impossible de copier ${label}.`);
    }
  };

  const handleDownload = (
    textGetter: () => string,
    extension: string,
    mime: string,
    label: string,
  ) => {
    const text = textGetter();
    triggerFileDownload(text, `${filenameBase}${extension}`, mime);
    toast.success(`Fichier ${label} téléchargé !`);
  };

  const isSm = size === "sm";

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {/* EasyWorship Exporter Pill */}
      <div className="hidden sm:inline-flex items-center rounded-xl bg-slate-900/80 border border-slate-700/60 overflow-hidden shadow-sm hover:border-indigo-500/40 hover:bg-slate-800/80 transition-all backdrop-blur-sm group/ew">
        <button
          type="button"
          onClick={() =>
            handleDownload(
              () => buildEasyWorshipTxt(hymn),
              ".txt",
              "text/plain;charset=utf-8",
              "EasyWorship (.txt)",
            )
          }
          className={`${isSm ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs"} font-semibold text-slate-400 group-hover/ew:text-slate-200 flex items-center gap-1.5 transition-colors`}
          title="Télécharger les paroles au format EasyWorship (.txt)"
        >
          <Download
            className={`${isSm ? "w-3 h-3" : "w-3.5 h-3.5"} text-slate-500 group-hover/ew:text-indigo-400 transition-colors`}
          />
          <span>EasyWorship</span>
        </button>
        <button
          type="button"
          onClick={() =>
            handleCopy("ew", () => buildEasyWorshipTxt(hymn), "EasyWorship")
          }
          className={`${isSm ? "px-2 py-1" : "px-2.5 py-1.5"} border-l border-slate-700/60 text-slate-500 hover:text-indigo-300 hover:bg-slate-800 transition-colors`}
          title="Copier les paroles au format EasyWorship"
        >
          {copiedType === "ew" ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400 whitespace-nowrap break-keep">
              <Check className={`${isSm ? "w-3 h-3" : "w-3.5 h-3.5"}`} />
              <span>Copié !</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs whitespace-nowrap break-keep">
              <Copy className={`${isSm ? "w-3 h-3" : "w-3.5 h-3.5"}`} />
              <span>Copier</span>
            </span>
          )}
        </button>
      </div>

      {/* OpenLP Exporter Pill */}
      <div className="hidden sm:inline-flex items-center rounded-xl bg-slate-900/80 border border-slate-700/60 overflow-hidden shadow-sm hover:border-emerald-500/40 hover:bg-slate-800/80 transition-all backdrop-blur-sm group/ol">
        <button
          type="button"
          onClick={() =>
            handleDownload(
              () => buildOpenLyricsXml(hymn),
              ".xml",
              "application/xml;charset=utf-8",
              "OpenLP (.xml)",
            )
          }
          className={`${isSm ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs"} font-semibold text-slate-400 group-hover/ol:text-slate-200 flex items-center gap-1.5 transition-colors`}
          title="Télécharger les paroles au format OpenLyrics XML pour OpenLP, ProPresenter, SongShow Plus (.xml)"
        >
          <Download
            className={`${isSm ? "w-3 h-3" : "w-3.5 h-3.5"} text-slate-500 group-hover/ol:text-emerald-400 transition-colors`}
          />
          <span>OpenLP</span>
        </button>
        <button
          type="button"
          onClick={() =>
            handleCopy("ol", () => buildOpenLyricsXml(hymn), "OpenLP XML")
          }
          className={`${isSm ? "px-2 py-1" : "px-2.5 py-1.5"} border-l border-slate-700/60 text-slate-500 hover:text-emerald-300 hover:bg-slate-800 transition-colors`}
          title="Copier les paroles au format OpenLyrics XML"
        >
          {copiedType === "ol" ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400 whitespace-nowrap break-keep">
              <Check className={`${isSm ? "w-3 h-3" : "w-3.5 h-3.5"}`} />
              <span>Copié !</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs whitespace-nowrap break-keep">
              <Copy className={`${isSm ? "w-3 h-3" : "w-3.5 h-3.5"}`} />
              <span>Copier</span>
            </span>
          )}
        </button>
      </div>

      {/* CCLI / Text Exporter Pill */}
      <div className="inline-flex items-center rounded-xl bg-slate-900/80 border border-slate-700/60 overflow-hidden shadow-sm hover:border-amber-500/40 hover:bg-slate-800/80 transition-all backdrop-blur-sm group/ccli">
        <button
          type="button"
          onClick={() =>
            handleDownload(
              () => buildCcliTxt(hymn),
              "_ccli.txt",
              "text/plain;charset=utf-8",
              "Texte Brut (.txt)",
            )
          }
          className={`${isSm ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs"} font-semibold text-slate-400 group-hover/ccli:text-slate-200 flex items-center gap-1.5 transition-colors`}
          title="Télécharger les paroles au format Texte Brut (.txt)"
        >
          <Download
            className={`${isSm ? "w-3 h-3" : "w-3.5 h-3.5"} text-slate-500 group-hover/ccli:text-amber-400 transition-colors`}
          />
          <span>Texte Brut</span>
        </button>
        <button
          type="button"
          onClick={() =>
            handleCopy("ccli", () => buildCcliTxt(hymn), "Texte Brut")
          }
          className={`${isSm ? "px-2 py-1" : "px-2.5 py-1.5"} border-l border-slate-700/60 text-slate-500 hover:text-amber-300 hover:bg-slate-800 transition-colors`}
          title="Copier les paroles au format CCLI / Texte Brut"
        >
          {copiedType === "ccli" ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400 whitespace-nowrap break-keep">
              <Check className={`${isSm ? "w-3 h-3" : "w-3.5 h-3.5"}`} />
              <span>Copié !</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs whitespace-nowrap break-keep">
              <Copy className={`${isSm ? "w-3 h-3" : "w-3.5 h-3.5"}`} />
              <span>Copier</span>
            </span>
          )}
        </button>
      </div>
    </div>
  );
};
